import DeviceInfo from "@/models/deviceInfo.model";
import Vue from "vue";
import { MutationTree } from "vuex";
import { State } from "./state";
import Settings from "@/models/settings.model";
import devices from "@/store/devices/index";

export enum MutationTypes {
  SetDevices = "SET_DEVICES",
  AddLiveMessage = "ADD_LIVE_MESSAGE",
  SetLiveEventsConnected = "SET_LIVE_EVENTS_CONNECTED",
  SetSelectedDevices = "SET_SELECTED_DEVICES",
  SetSettings = "SET_SETTINGS",
  SetRecoveryStarted = "SET_RECOVERY_STARTED",
  SetRecoveryStopped = "SET_RECOVERY_STOPPED",
  SetIdentificationStarted = "SET_IDENTIFICATION_STARTED",
  SetIdentificationStopped = "SET_IDENTIFICATION_STOPPED",
  SetLocationRequested = "SET_LOCATION_REQUESTED",
  SetLifetimeRequested = "SET_LIFETIME_REQUESTED",
  SetDeviceToGetInfo = "SET_DEVICE_TO_GET_INFO",
  ChangeCenterLocation = "CHANGE_CENTER_LOCATION",
  UpdateDeviceLabel = "UPDATE_DEVICE_LABEL"
}

export const mutations: MutationTree<State> = {
  [MutationTypes.SetDevices](state, devicesInfo?: DeviceInfo[]) {
    Vue.set(state, "devices", devicesInfo ?? []);

    const visibleDevices = (state.devices ?? [])
      .filter((device) => device.status?.reported?.location);
    if (visibleDevices.length) {
      state.centerLocation = {
        lat: visibleDevices[0].status.reported.location.data.lat,
        lng: visibleDevices[0].status.reported.location.data.lng,
      };
    }
  },
  [MutationTypes.AddLiveMessage](state, message?: any) {
    const deviceIdx = state.devices.findIndex((dev) => dev.id === message.Device);
    if (deviceIdx >= 0) {
      const device = state.devices[deviceIdx];
      device.live = true;
      if (message.NotificationType.indexOf("LOCATION_RECEIVED") >= 0) {
        const location = {
          date: message.Data.LocationTime,
          data: {
            locationType: message.NotificationType.substring(0, 4),
            accuracy: message.Data.Accuracy,
            lat: message.Data.Latitude,
            lng: message.Data.Longitude,
            locationTime: message.Data.LocationTime
          }
        };
        device.status.reported.location = location;
      }
    }
    state.events.push(message);
  },
  [MutationTypes.SetLiveEventsConnected](state, connected: boolean) {
    state.liveDataConnected = connected;
  },
  [MutationTypes.SetSelectedDevices](state, devicesInfo: DeviceInfo[]) {
    state.devices.forEach((dev) => {
      dev.visible = devicesInfo.indexOf(dev) >= 0;
    });

    const visibleDevices = (state.devices ?? [])
      .filter((device) => device.visible && device.status?.reported?.location);
    if (visibleDevices.length) {
      state.centerLocation = {
        lat: visibleDevices[0].status.reported.location.data.lat,
        lng: visibleDevices[0].status.reported.location.data.lng,
      };
    }
  },
  [MutationTypes.SetSettings](state, settings?: Settings) {
    state.settings = settings ?? new Settings({});
  },
  [MutationTypes.SetRecoveryStarted](state, device?: DeviceInfo) {
    if (device) {
      const existingDevice = state.devices.find((d) => d.id === device.id);
      if (existingDevice) {
        existingDevice.status.requested.recovery = device.status.requested.recovery;
        existingDevice.status.requested.lifetime = device.status.requested.lifetime;
        existingDevice.updateCurrentStatus();
      }
    }
  },
  [MutationTypes.SetRecoveryStopped](state, device?: DeviceInfo) {
    if (device) {
      const existingDevice = state.devices.find((d) => d.id === device.id);
      if (existingDevice) {
        existingDevice.status.requested.recovery = device.status.requested.recovery;
        existingDevice.status.requested.lifetime = device.status.requested.lifetime;
        existingDevice.updateCurrentStatus();
      }
    }
  },
  [MutationTypes.SetIdentificationStarted](state, device?: DeviceInfo) {
    if (device) {
      const existingDevice = state.devices.find((d) => d.id === device.id);
      if (existingDevice) {
        existingDevice.status.requested.identification = device.status.requested.identification;
        existingDevice.updateCurrentStatus();
      }
    }
  },
  [MutationTypes.SetIdentificationStopped](state, device?: DeviceInfo) {
    if (device) {
      const existingDevice = state.devices.find((d) => d.id === device.id);
      if (existingDevice) {
        existingDevice.status.requested.identification = device.status.requested.identification;
        existingDevice.updateCurrentStatus();
      }
    }
  },
  [MutationTypes.SetLocationRequested](state, device?: DeviceInfo) {
    if (device) {
      const existingDevice = state.devices.find((d) => d.id === device.id);
      if (existingDevice) {
        existingDevice.status.requested.location = device.status.requested.location;
        existingDevice.updateCurrentStatus();
      }
    }
  },
  [MutationTypes.SetLifetimeRequested](state, device?: DeviceInfo) {
    if (device) {
      const existingDevice = state.devices.find((d) => d.id === device.id);
      if (existingDevice) {
        existingDevice.status.requested.lifetime = device.status.requested.lifetime;
        existingDevice.updateCurrentStatus();
      }
    }
  },
  [MutationTypes.SetDeviceToGetInfo](state, device: string) {
    state.selectedDevice = device;
  },
  [MutationTypes.ChangeCenterLocation](state, location: { lat: number, lng: number } | undefined) {
    state.centerLocation = location;
  },
  [MutationTypes.UpdateDeviceLabel](state, updateInfo: { serialNumber: string, label: string }) {
    const existingDevice = state.devices.find((d) => d.id === updateInfo.serialNumber);
    if (existingDevice) {
      existingDevice.label = updateInfo.label;
    }
  },
};
